/**
 * Utilidades para optimización y procesamiento de imágenes
 * Incluye redimensionamiento, compresión y conversión de formatos
 */

export interface ImageOptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 - 1.0
  format?: 'jpeg' | 'png' | 'webp';
  maintainAspectRatio?: boolean;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageProcessingResult {
  success: boolean;
  dataUrl?: string;
  blob?: Blob;
  error?: string;
  originalSize?: number;
  compressedSize?: number;
  compressionRatio?: number;
}

/**
 * Carga una imagen desde un archivo o URL y la convierte a HTMLImageElement
 */
export const loadImage = (src: string | File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Error al cargar la imagen'));
    
    if (src instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(src);
    } else {
      img.src = src;
    }
  });
};

/**
 * Redimensiona una imagen manteniendo la relación de aspecto
 */
export const resizeImage = (
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
  maintainAspectRatio: boolean = true
): { width: number; height: number } => {
  let { width, height } = img;

  if (!maintainAspectRatio) {
    return { width: maxWidth, height: maxHeight };
  }

  // Calcular las nuevas dimensiones manteniendo la relación de aspecto
  const aspectRatio = width / height;

  if (width > height) {
    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }
  } else {
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
  }

  // Asegurar que no exceda los límites
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return { width: Math.round(width), height: Math.round(height) };
};

/**
 * Recorta una imagen según el área especificada
 */
export const cropImage = (
  img: HTMLImageElement,
  cropArea: CropArea,
  outputWidth: number = 500,
  outputHeight: number = 500
): Promise<ImageProcessingResult> => {
  return new Promise((resolve) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve({ success: false, error: 'No se pudo crear el contexto del canvas' });
        return;
      }

      // Configurar el canvas de salida
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      // Calcular las coordenadas de recorte en píxeles reales
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;

      const sourceX = cropArea.x * scaleX;
      const sourceY = cropArea.y * scaleY;
      const sourceWidth = cropArea.width * scaleX;
      const sourceHeight = cropArea.height * scaleY;

      // Dibujar la imagen recortada y redimensionada
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );

      // Convertir a blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            resolve({
              success: true,
              dataUrl,
              blob,
              compressedSize: blob.size
            });
          } else {
            resolve({ success: false, error: 'Error al generar el blob' });
          }
        },
        'image/jpeg',
        0.9
      );
    } catch (error) {
      resolve({ success: false, error: `Error al procesar la imagen: ${error}` });
    }
  });
};

/**
 * Optimiza una imagen con las opciones especificadas
 */
export const optimizeImage = (
  img: HTMLImageElement,
  options: ImageOptimizationOptions = {}
): Promise<ImageProcessingResult> => {
  return new Promise((resolve) => {
    try {
      const {
        maxWidth = 500,
        maxHeight = 500,
        quality = 0.9,
        format = 'jpeg',
        maintainAspectRatio = true
      } = options;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve({ success: false, error: 'No se pudo crear el contexto del canvas' });
        return;
      }

      // Calcular nuevas dimensiones
      const { width, height } = resizeImage(img, maxWidth, maxHeight, maintainAspectRatio);

      // Configurar el canvas
      canvas.width = width;
      canvas.height = height;

      // Mejorar la calidad del redimensionamiento
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Dibujar la imagen redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Determinar el tipo MIME
      const mimeType = format === 'png' ? 'image/png' : 
                     format === 'webp' ? 'image/webp' : 'image/jpeg';

      // Convertir a blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const dataUrl = canvas.toDataURL(mimeType, quality);
            const originalSize = img.src.length * 0.75; // Estimación aproximada
            const compressionRatio = originalSize > 0 ? blob.size / originalSize : 1;

            resolve({
              success: true,
              dataUrl,
              blob,
              originalSize,
              compressedSize: blob.size,
              compressionRatio
            });
          } else {
            resolve({ success: false, error: 'Error al generar el blob optimizado' });
          }
        },
        mimeType,
        quality
      );
    } catch (error) {
      resolve({ success: false, error: `Error al optimizar la imagen: ${error}` });
    }
  });
};

/**
 * Convierte un blob a data URL
 */
export const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Error al convertir blob a data URL'));
    reader.readAsDataURL(blob);
  });
};

/**
 * Convierte un data URL a blob
 */
export const dataUrlToBlob = (dataUrl: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      
      resolve(new Blob([u8arr], { type: mime }));
    } catch (error) {
      reject(new Error('Error al convertir data URL a blob'));
    }
  });
};

/**
 * Obtiene información sobre una imagen
 */
export const getImageInfo = (img: HTMLImageElement) => {
  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
    aspectRatio: img.naturalWidth / img.naturalHeight,
    displayWidth: img.width,
    displayHeight: img.height,
    src: img.src
  };
};

/**
 * Valida si un archivo es una imagen válida
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Verificar tipo de archivo
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'El archivo debe ser una imagen' };
  }

  // Verificar tipos soportados
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!supportedTypes.includes(file.type)) {
    return { valid: false, error: 'Formato de imagen no soportado. Use JPEG, PNG o WebP' };
  }

  // Verificar tamaño (máximo 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return { valid: false, error: 'La imagen es demasiado grande. Máximo 10MB' };
  }

  return { valid: true };
};

/**
 * Formatea el tamaño de archivo en bytes a una cadena legible
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
